/**
 * Admin Operation Costs Service
 * Frontend service for managing configurable operation costs
 */

import axios from 'axios';
import { currencyFormattingService } from './currency-formatting.service';
import { adminService, ManufacturerDetails } from './admin.service';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

export interface CostConfiguration {
  id: string;
  operationType: string;
  costType: 'per_product' | 'fixed' | 'tiered';
  baseRate: number;
  baseCoverageMonths?: number;
  monthlySurchargePercent?: number;
  currency: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export interface ConfigurationAudit {
  id: string;
  operationType: string;
  changeType: 'create' | 'update' | 'delete';
  oldValue: number | null;
  newValue: number;
  changedBy: string;
  changedAt: string;
  reason?: string;
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  count?: number;
  message?: string;
  error?: string;
}

export interface ManufacturerOverride {
  id: string;
  manufacturerId: string;
  operationType: string;
  discountType: 'percent' | 'fixed';
  discountValue: number;
  maxUnits?: number | null;
  applicableUnits?: number | null;
  isActive: boolean;
  startsAt?: string | null;
  endsAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

class AdminOperationCostsService {
  private getAuthHeader() {
    const token = localStorage.getItem('auth_token');
    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  }

  /**
   * Get all cost configurations
   */
  async getAllConfigurations(): Promise<CostConfiguration[]> {
    const response = await axios.get<ApiResponse<CostConfiguration[]>>(
      `${API_URL}/admin/operation-costs`,
      this.getAuthHeader()
    );
    
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to fetch configurations');
    }
    
    return response.data.data;
  }

  /**
   * Get cost configuration for a specific operation type
   */
  async getConfiguration(operationType: string): Promise<CostConfiguration> {
    const response = await axios.get<ApiResponse<CostConfiguration>>(
      `${API_URL}/admin/operation-costs/${operationType}`,
      this.getAuthHeader()
    );
    
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to fetch configuration');
    }
    
    return response.data.data;
  }

  /**
   * Update cost configuration for a specific operation type
   */
  async updateConfiguration(
    operationType: string,
    baseRate: number,
    reason?: string,
    baseCoverageMonths?: number,
    monthlySurchargePercent?: number
  ): Promise<CostConfiguration> {
    const response = await axios.put<ApiResponse<CostConfiguration>>(
      `${API_URL}/admin/operation-costs/${operationType}`,
      {
        baseRate,
        reason,
        baseCoverageMonths,
        monthlySurchargePercent,
      },
      this.getAuthHeader()
    );
    
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to update configuration');
    }
    
    return response.data.data;
  }

  /**
   * Get audit history for all configurations
   */
  async getAuditHistory(): Promise<ConfigurationAudit[]> {
    const response = await axios.get<ApiResponse<ConfigurationAudit[]>>(
      `${API_URL}/admin/operation-costs/history/all`,
      this.getAuthHeader()
    );
    
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to fetch audit history');
    }
    
    return response.data.data;
  }

  /**
   * Get audit history for a specific operation type
   */
  async getOperationAuditHistory(operationType: string): Promise<ConfigurationAudit[]> {
    const response = await axios.get<ApiResponse<ConfigurationAudit[]>>(
      `${API_URL}/admin/operation-costs/${operationType}/history`,
      this.getAuthHeader()
    );
    
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to fetch audit history');
    }
    
    return response.data.data;
  }

  /**
   * Calculate cost for a given operation and quantity
   */
  async calculateCost(operationType: string, quantity: number): Promise<{
    operationType: string;
    quantity: number;
    perUnitCost: number;
    totalCost: number;
    currency: string;
    coverageMonths?: number;
    extraMonths?: number;
    surchargePerUnit?: number;
    surchargeTotal?: number;
  }> {
    const response = await axios.post(
      `${API_URL}/operation-costs/calculate`,
      {
        operationType,
        quantity,
      },
      this.getAuthHeader()
    );
    
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to calculate cost');
    }
    
    return response.data.data;
  }

  /**
   * Format currency value for display
   */
  formatCurrency(amount: number, currency: string = 'INR'): string {
    return currencyFormattingService.formatCurrency(amount, currency);
  }

  /**
   * Format currency range for display
   */
  formatCurrencyRange(minAmount: number, maxAmount: number, currency: string = 'INR'): string {
    return currencyFormattingService.formatCurrencyRange(minAmount, maxAmount, currency);
  }

  /**
   * Validate currency amount
   */
  validateAmount(amount: number, options?: {
    minAmount?: number;
    maxAmount?: number;
    allowZero?: boolean;
  }): { isValid: boolean; error?: string } {
    return currencyFormattingService.validateAmount(amount, options);
  }

  /**
   * Helper to fetch manufacturers for dropdown (name + id)
   */
  async listManufacturers(): Promise<ManufacturerDetails[]> {
    return await adminService.getAllManufacturers();
  }

  // ====== Overrides ======
  async listOverrides(params?: { manufacturerId?: string; operationType?: string; isActive?: boolean }): Promise<ManufacturerOverride[]> {
    const response = await axios.get<ApiResponse<ManufacturerOverride[]>>(
      `${API_URL}/admin/operation-costs/overrides`,
      {
        ...this.getAuthHeader(),
        params,
      }
    );
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to fetch overrides');
    }
    return response.data.data;
  }

  async createOverride(payload: Partial<ManufacturerOverride> & { manufacturerId: string; operationType: string; discountType: 'percent' | 'fixed'; discountValue: number }): Promise<ManufacturerOverride> {
    const response = await axios.post<ApiResponse<ManufacturerOverride>>(
      `${API_URL}/admin/operation-costs/overrides`,
      payload,
      this.getAuthHeader()
    );
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to create override');
    }
    return response.data.data;
  }

  async updateOverride(id: string, payload: Partial<ManufacturerOverride>): Promise<ManufacturerOverride> {
    const response = await axios.put<ApiResponse<ManufacturerOverride>>(
      `${API_URL}/admin/operation-costs/overrides/${id}`,
      payload,
      this.getAuthHeader()
    );
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to update override');
    }
    return response.data.data;
  }
}

export const adminOperationCostsService = new AdminOperationCostsService();
